import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { useUuid } from '~/eds/src/hooks/useUuid';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { calculateCreditEstimate } from '../billing/utils';
import { PUBLISH_ELEMENT_ID } from '../constants';
import { TargetEntityDetails } from '../types/entity';

interface Props {
  isVisible: boolean;
  modelId: Uuid;
  targetEntityDetails: Nullable<TargetEntityDetails>;
  version: number;
}

export const ConfirmResumePublishQuotaModal = ({
  isVisible,
  modelId,
  version,
  targetEntityDetails,
}: Props) => {
  const [id] = useUuid();

  const { toast } = useToast();

  const dispatch = useDispatch();
  const intl = useIntl();

  const {
    data: modelProgress = {
      docsProcessed: 0,
      docsInScope: 0,
    },
    isLoading: isProgressLoading,
  } = api.endpoints.getPromptModelProgress.useQuery({
    modelId,
  });

  const { docsInScope, docsProcessed } = modelProgress;

  const newDocsInScope = calculateCreditEstimate(docsProcessed, docsInScope);

  const [publishFieldModel, { isLoading: isPublishingFieldModel }] =
    api.endpoints.publishPromptModel.useMutation();

  const handlePublish = () => {
    publishFieldModel({
      modelId,
      version,
      idempotencyKey: id,
      publishMode: 'new_docs_in_scope',
    })
      .unwrap()
      .then(
        () => {
          toast({
            message: intl.formatMessage(
              {
                id: 'ConfirmResumePublishQuotaModal.publishSuccessMessage',
                defaultMessage:
                  '"{targetEntityLabel}" is active and is processing documents.',
                description:
                  'Success message when model is published and processing documents. Example: targetEntityLabel = "Contract Value Model"',
              },
              {
                targetEntityLabel: targetEntityDetails?.label ?? '–',
              },
            ),
            status: 'success',
          });
          dispatch(slices.xRay.actions.setModelAction(null));
        },
        () =>
          toast({
            message: intl.formatMessage({
              id: 'ConfirmResumePublishQuotaModal.publishErrorMessage',
              defaultMessage:
                'There was an error publishing the model. Please try again.',
              description: 'Error message when model publishing fails',
            }),
            status: 'danger',
          }),
      );
  };

  return (
    <Modal
      children={
        <Text id={PUBLISH_ELEMENT_ID}>
          <FormattedMessage
            id="ConfirmResumePublishQuotaModal.republishDescription"
            defaultMessage="This applies edits to <strong>{newDocsInScope} unprocessed documents</strong>. Re-publishing models <strong>will not consume</strong> another model quota."
            description="Description of republishing model and quota usage. Example: newDocsInScope = '25'"
            values={{
              newDocsInScope,
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </Text>
      }
      isVisible={isVisible}
      loadingContent={{
        isLoading: isProgressLoading,
        message: intl.formatMessage({
          id: 'ConfirmResumePublishQuotaModal.loadingMessage',
          defaultMessage: 'Loading progress...',
          description: 'Loading message while fetching progress',
        }),
      }}
      title={intl.formatMessage({
        id: 'ConfirmResumePublishQuotaModal.title',
        defaultMessage: 'Confirm to Re-publish',
        description: 'Modal title for confirming model republishing',
      })}
      primaryAction={{
        disabled: isProgressLoading,
        isLoading: isPublishingFieldModel,
        text: intl.formatMessage({
          id: 'ConfirmResumePublishQuotaModal.republishButton',
          defaultMessage: 'Re-publish',
          description: 'Re-publish button text',
        }),
        onClick: handlePublish,
      }}
      onHide={() => {
        dispatch(slices.xRay.actions.setModelAction(null));
      }}
    />
  );
};
